import { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "pb_welcome_banner_dismissed_v1";

/**
 * Sticky "Save 10% — code WELCOME10" banner shown on /shop.
 *
 * Behavior:
 * - Hidden by default until a tiny mount delay (avoids layout flash on slow paints).
 * - Dismissed state persists in localStorage so returning visitors don't see it again.
 * - Tap the code to copy to clipboard; toast confirms.
 * - Hidden in print.
 */
export default function WelcomeBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {
      /* noop */
    }
    const t = setTimeout(() => setVisible(true), 250);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* noop */
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText("WELCOME10");
      toast.success("Code copied — paste it at checkout.");
    } catch {
      toast.info("Code: WELCOME10");
    }
  };

  return (
    <div
      data-testid="welcome-banner"
      className="no-print sticky top-[64px] z-30 flex items-center justify-center gap-3 px-4 py-2.5 text-sm font-bold text-white shadow-[0_8px_20px_rgba(242,106,52,0.25)]"
      style={{
        background:
          "linear-gradient(90deg, #F26A34 0%, #E5484D 55%, #4E2C80 100%)",
      }}
    >
      <Sparkles className="h-4 w-4 text-pb-sun flex-shrink-0" />
      <p className="text-center leading-tight">
        First time here? <span className="font-extrabold">Save 10%</span> with code{" "}
        <button
          type="button"
          onClick={copy}
          data-testid="welcome-banner-code"
          className="inline-flex items-center font-display font-black tracking-wider bg-white/95 text-pb-coral rounded-md px-2 py-0.5 ml-1 hover:scale-105 transition"
          aria-label="Copy WELCOME10 code"
        >
          WELCOME10
        </button>
      </p>
      <button
        type="button"
        onClick={dismiss}
        data-testid="welcome-banner-dismiss"
        aria-label="Dismiss"
        className="ml-1 sm:ml-2 rounded-full p-1 hover:bg-white/15 transition flex-shrink-0"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
